<template>
  <div class="profile-header">
    <div class="banner" :class="{canUpload}" @click="handleBannerClick">
      <img v-if="imageUrl" :src="imageUrl" class="banner-img" alt="" @load="checkAspectRatio" />
      <div v-if="imageUrl && isNot16By9" class="aspect-notice">
        Click to upload a 16:9 image
      </div>
      <div v-else class="banner-placeholder" />
    </div>
    <div class="meta-wrapper">
      <AppModalProfileMeta />
    </div>
  </div>
</template>

<script setup>
import {computed, ref} from "vue";
import {useRouter} from "vue-router";
import {useFileDialog} from "@vueuse/core";
import AppModalProfileMeta from "@/components/App/Modal/Profile/ProfileMeta";
import {useAvatarCropState} from "@/composables/useAvatarCropState";
import useUserSafe from "@/composables/useUserSafe";

const router = useRouter();
const {user} = useUserSafe();
const {setFile} = useAvatarCropState();

// 16:9 within a pixel (Clerk's resized image may round the height)
function getIs16By9(width, height) {
  return Math.abs((width * 9) / 16 - height) < 1;
}

const isNot16By9 = ref(false);

function checkAspectRatio(event) {
  const {naturalWidth, naturalHeight} = event.target;
  isNot16By9.value = !getIs16By9(naturalWidth, naturalHeight);
}

const canUpload = computed(() => typeof user.value?.setProfileImage === "function");

const imageUrl = computed(() => {
  const url = user.value?.imageUrl;
  if (!url) return null;
  return `${url}?width=1920&quality=85`;
});

const {open: openFileDialog, onChange} = useFileDialog({accept: "image/*", multiple: false});
onChange((files) => {
  const file = files?.[0];
  if (!file) return;
  setFile(file);
  router.push({hash: "#crop-image"});
});

function handleBannerClick() {
  if (canUpload.value) openFileDialog();
}
</script>

<style scoped>
.profile-header {
  margin: -10px 0 0; /* no negative side margins: wider than the scroll area, it let the dialog
                       scroll sideways when a field took focus (and stay shifted) */
}
.banner {
  position: relative; /* the aspect notice sits on top of the image */
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: radial-gradient(circle at 30% 25%, #8b5cf6, #f59e0b);
}
.banner-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
/* the bio bar's look (RichTextEditor: #555, 24px, white 13px semi-bold), 75% opaque */
.aspect-notice {
  position: absolute;
  top: 10px; /* below the dialog header, which covers the photo's top 10px (.profile-header) */
  left: 0;
  right: 0;
  height: 24px;
  padding: 0 12px;
  background: rgba(85, 85, 85, 0.75);
  color: white;
  font-size: 13px;
  font-weight: 600;
  line-height: 24px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.banner.canUpload {
  cursor: pointer;
}
.meta-wrapper {
  position: relative; /* edit layers dock here, directly under the photo */
  padding: 8px 0 4px;
}
.banner.canUpload:hover {
  filter: brightness(0.95); /* opens the file picker (DESIGN.md § 15) */
}
</style>
